import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Link2 } from 'lucide-react';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '../../../../empreenderUI/ui/Drawer';
import { Button } from '../../../../empreenderUI/ui/Button';
import Input from '../../../../empreenderUI/ui/Input';
import { translations } from '../../../../utils/language';

const ModalCadLinkNew = (props) => {
  const dispatch = useDispatch();
  const reducerControlVariants = useSelector((state) => state.reducerControlVariants);
  const language = useSelector((state) => state.reducerLanguage);

  const [inputName, setInputName] = useState('');
  const [inputUrl, setInputUrl] = useState('');
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);

  const closeModal = () => {
    props.setVisible(false);
    setLinks([]);
    setInputName('');
    setInputUrl('');
    dispatch({ type: 'VARIANTS_CONTROL_RESET' });
    props.close?.();
  };

  const handleOpenChange = (open) => {
    if (!open) closeModal();
  };

  const cadLink = async () => {
    if (!inputName || !inputUrl) return;

    setLoading(true);

    try {
      const newUrl = {
        limit: 0,
        message: '',
        media_name: inputName,
        url: inputUrl.trim(),
        media_extname: 'url',
        media_size: null,
      };
      const token = JSON.parse(localStorage.getItem('depoimentos@login'))?.access_token.token;
      const url = `${process.env.REACT_APP_API_URL}/upload_media_and_link`;
      const headers = new Headers({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      });
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(newUrl),
      });
      const result = await response.json();
      const id_url = result.data.id;

      await Promise.all(
        reducerControlVariants.data.map((variant) =>
          fetch(`${process.env.REACT_APP_API_URL}/link_media_product`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              media_id: id_url,
              product_id: variant.variants.product_id,
              variant_id: variant.variants.variant_id,
            }),
          }).then((res) => {
            if (!res.ok) {
              throw new Error(`Erro na requisição para variante ${variant.variant_id}: ${res.statusText}`);
            }
            return res.json();
          }),
        ),
      );

      setLinks([...links, { name: inputName, url: inputUrl }]);
      setInputUrl('');
      setInputName('');
      props.goSearch?.();
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = inputName !== '' && inputUrl !== '';
  const subtitle = props.contextLabel || translations['products-link-desc'][language.data];

  return (
    <Drawer open={!!props.visible} onOpenChange={handleOpenChange} direction='right'>
      <DrawerContent className='!w-[min(420px,100vw)] !max-w-[min(420px,100vw)] !h-full !max-h-[100dvh] border-l border-gray-200 p-0 flex flex-col overflow-hidden bg-white'>
        <DrawerHeader className='relative shrink-0 space-y-0 bg-white px-6 pt-6 pb-2'>
          <div className='flex items-start justify-between gap-3 pr-10'>
            <div className='min-w-0 space-y-1'>
              <DrawerTitle className='text-xl font-bold tracking-tight text-gray-900'>{translations['products-link-titulo'][language.data]}</DrawerTitle>
              <DrawerDescription className='text-sm font-medium text-gray-500'>{subtitle}</DrawerDescription>
            </div>
          </div>
          <DrawerClose asChild>
            <button type='button' className='absolute right-4 top-4 inline-flex size-8 items-center justify-center rounded-lg bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200' aria-label={translations['common-close'][language.data]}>
              <XMarkIcon className='size-5' />
            </button>
          </DrawerClose>
        </DrawerHeader>

        <div className='flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-4 custom-scroll'>
          <div className='flex flex-col gap-1.5'>
            <label className='text-xs font-medium text-gray-500'>{translations['products-link-nome'][language.data]}</label>
            <Input type='text' value={inputName} onChange={(e) => setInputName(e.target.value)} placeholder={translations['products-link-nome-placeholder'][language.data]} />
          </div>

          <div className='flex flex-col gap-1.5'>
            <label className='text-xs font-medium text-gray-500'>{translations['products-link-url'][language.data]}</label>
            <Input type='text' value={inputUrl} onChange={(e) => setInputUrl(e.target.value)} placeholder={translations['products-link-url-placeholder'][language.data]} />
          </div>

          <div className='flex justify-end'>
            <Button type='button' disabled={!canSubmit || loading} onClick={cadLink} className='bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50'>
              {loading ? translations['Aguarde...'][language.data] : translations['products-link-cadastrar'][language.data]}
            </Button>
          </div>

          {links.length > 0 && (
            <div className='mt-2 flex flex-col gap-3 border-t border-gray-100 pt-4'>
              <label className='text-xs font-medium text-gray-500'>{translations['products-link-cadastrados'][language.data]}</label>
              {links.map((item, index) => (
                <CardLink key={`${item.url}-${index}`} item={item} />
              ))}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default ModalCadLinkNew;

const CardLink = ({ item }) => {
  const language = useSelector((state) => state.reducerLanguage);
  const href = /^https?:\/\//i.test(item.url) ? item.url : `https://${item.url}`;

  return (
    <div className='relative flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 transition-colors hover:border-blue-300 hover:bg-blue-50/40'>
      <a href={href} target='_blank' rel='noopener noreferrer' className='absolute inset-0 z-10 rounded-lg' aria-label={`${translations['common-open'][language.data]} ${item.name || item.url}`} />
      <span className='inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600'>
        <Link2 className='size-4' strokeWidth={1.75} />
      </span>
      <div className='min-w-0 flex-1'>
        <p className='truncate text-sm font-medium text-gray-900'>{item.name}</p>
        <p className='block truncate text-xs text-gray-500'>{item.url}</p>
      </div>
    </div>
  );
};
